import { useIsCurrentStudentArchived } from '@/hooks'
import { updateReflection } from '@/utils/data'
import { getTheme } from '@/utils/theme'
import { moment } from '@seiue/moment'
import { Button, Form, Toast } from '@seiue/ui'
import { Row } from 'antd'
import humps from 'humps'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import {
  FieldDomainEnum,
  FieldGroupRes,
  ReflectionStatusEnum,
} from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'
import { IFormItemTypeEnum, genderToLabel, statusToLabel } from 'packages/utils'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { AvatarUpload } from './AvatarUpload'
import { ViewForm } from './components/ViewForm'
import { useUserDetailContext } from './context'
import { IConfig, IFixedInfo } from './types'

const theme = getTheme()

/**
 * 用户固定信息
 *
 * @param props - 参数
 * @param props.data - 固定信息
 * @returns 组件
 */
export const FixedInfo: React.FC<{
  data: IFixedInfo
  isOwner?: boolean
}> = (props) => {
  const { domain, id, updateInfo, groupsData } = useUserDetailContext()
  const { data, isOwner } = props
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [form] = Form.useForm()
  const isStudentArchived = useIsCurrentStudentArchived()

  useEffect(() => {
    const finalData = {
      ...data,
      registedAt: data.registedAt
        ? moment(data.registedAt, 'YYYY-MM-DD HH:mm')
        : undefined,
      admissionedAt: data.admissionedAt
        ? moment(data.admissionedAt, 'YYYY-MM-DD HH:mm')
        : undefined,
      archivedAt: data.archivedAt
        ? moment(data.archivedAt, 'YYYY-MM-DD HH:mm')
        : undefined,
    }
    isEdit && form.setFieldsValue(finalData)
  }, [data, isEdit])

  const isFormalStudent = FieldDomainEnum.FormalStudent === domain

  const fixedInfoConfig: IConfig[] = [
    {
      type: IFormItemTypeEnum.Input,
      label: '姓      名',
      name: 'name',
    },
    {
      type: IFormItemTypeEnum.Select,
      label: '性      别',
      name: 'gender',
      options: genderToLabel.listOptions('value', 'label'),
    },
    [FieldDomainEnum.FormalTeacher].includes(domain)
      ? {
          type: IFormItemTypeEnum.Input,
          label: '学      科',
          name: 'subject',
        }
      : {
          type: IFormItemTypeEnum.Input,
          label: '届      别',
          name: 'graduate',
        },

    {
      type: IFormItemTypeEnum.Input,
      label: '手机号码',
      name: 'phone',
    },

    [FieldDomainEnum.FormalTeacher, FieldDomainEnum.FormalStudent].includes(
      domain
    )
      ? {
          type: IFormItemTypeEnum.Time,
          label:
            data.status === ReflectionStatusEnum.Archived
              ? `${isFormalStudent ? '离校' : '离职'}时间`
              : `${isFormalStudent ? '入校' : '入职'}时间`,
          name: data.archivedAt ? 'archivedAt' : 'admissionedAt',
          disabled: true,
        }
      : {
          type: IFormItemTypeEnum.Time,
          label: '注册时间',
          name: 'registedAt',
          disabled: true,
        },

    ...(!isOwner
      ? [
          {
            type: IFormItemTypeEnum.Select,
            label: '账号状态',
            name: 'status',
            options: statusToLabel.listOptions('value', 'label'),
          },
        ]
      : []),
  ]

  const fixedFieldGroup: FieldGroupRes | undefined = groupsData?.filter(
    (e) => e.name === '固定信息'
  )?.[0]

  const resInfos = isOwner
    ? fixedInfoConfig.map((config) => {
        return {
          ...config,
          disabled: !fixedFieldGroup?.fields?.find(
            (field) => humps.camelize(field.name) === config.name
          )?.editableFrontend,
        }
      })
    : fixedInfoConfig

  const onSaveEdit = () => {
    form.validateFields().then(async (values) => {
      const finalData = {
        ...values,
        registedAt: values.registedAt
          ? moment(values.registedAt).format('YYYY-MM-DD HH:mm')
          : undefined,
        admissionedAt: values.admissionedAt
          ? moment(values.admissionedAt).format('YYYY-MM-DD HH:mm')
          : undefined,
        archivedAt: values.archivedAt
          ? moment(values.archivedAt).format('YYYY-MM-DD HH:mm')
          : undefined,
      }

      if (!updateInfo) return
      await updateInfo({
        id,
        payload: {
          ...finalData,
        },
      })

      updateReflection({
        modifyKeys: ['name', 'phone'],
        newData: {
          phone: finalData.phone,
          name: finalData.name,
        },
      })

      Toast.success('保存成功')
      setIsEdit(false)
    })
  }

  const onCancelEdit = () => {
    form.resetFields()
    setIsEdit(false)
  }

  const renderBtns = () => {
    if (isEdit) {
      return (
        <BtnsWrapper>
          <Button
            type="primary"
            onClick={() => onSaveEdit()}
          >
            保存
          </Button>
          <Button onClick={() => onCancelEdit()}>取消</Button>
        </BtnsWrapper>
      )
    }
    return (
      <StyledButton
        type="link"
        onClick={() => setIsEdit(true)}
      >
        <GrowpIcon
          name="Edit"
          size={14}
          color={theme.brand.text0}
          hoverColor={theme.brand._1}
          className="bianji"
          text="编辑"
        />
      </StyledButton>
    )
  }

  return (
    <>
      <AvatarUpload
        name={data?.name?.slice(-2) || '-'}
        src={data?.avatar || ''}
        isEdit={isEdit}
      />
      <RightWrapper>
        <TopRightForm>
          <Form
            labelCol={{ span: 5, offset: 0 }}
            labelAlign="right"
            colon={false}
            form={form}
            requiredMark={false}
          >
            <EntryComponentsWrapper>
              <Row gutter={24}>
                <ViewForm
                  isEdit={isEdit}
                  configs={resInfos}
                  colSpan={12}
                  initData={data}
                  viewNotForm={true}
                />
              </Row>
            </EntryComponentsWrapper>
          </Form>
        </TopRightForm>
        <BtnWrapper>{isStudentArchived ? null : renderBtns()}</BtnWrapper>
      </RightWrapper>
    </>
  )
}

const TopRightForm = styled.div`
  width: 610px;
  &&& {
    .ant-form-item {
      margin-bottom: 12px;
    }
    .ant-form-item-control {
      max-width: 238px;
    }
    .ant-form-item-label {
      margin-right: 8px;
      white-space: pre;
    }
    .ant-form-item-label > label::after {
      margin-right: 0;
    }
  }
`

const RightWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  min-width: 726px;
  max-width: 980px;
  margin-left: 40px;
`

const StyledButton = styled(Button)``

const BtnWrapper = styled.div`
  width: 350px;
  height: 100%;
  padding-top: 88px;
  padding-left: 40px;
`

const BtnsWrapper = styled.div`
  display: flex;
  gap: 12px;
`
